import type {
  ApiResponse,
  BreadthData,
  ChanStructureData,
  DashboardData,
  InstrumentProfile,
  MemberPositionsData,
  OptionChainData,
  OptionSummaryData,
  StreamContextPayload,
  StreamQuotePayload,
  StreamReadyPayload,
  TermStructureData
} from '../types'

const requestBase = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '')
const REQUEST_TIMEOUT_MS = 12_000

export class ApiError extends Error {
  readonly status: number
  readonly errorCode?: string

  constructor(message: string, status: number, errorCode?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.errorCode = errorCode
  }
}

function apiUrl(path: string): string {
  return `${requestBase}${path}`
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<ApiResponse<T>> {
  const controller = new AbortController()
  let timedOut = false
  const timer = window.setTimeout(() => {
    timedOut = true
    controller.abort()
  }, REQUEST_TIMEOUT_MS)
  const forwardAbort = () => controller.abort(signal?.reason)
  signal?.addEventListener('abort', forwardAbort, { once: true })
  if (signal?.aborted) controller.abort(signal.reason)

  let response: Response
  try {
    response = await fetch(apiUrl(path), {
      headers: { Accept: 'application/json' },
      signal: controller.signal
    })
  } catch (error) {
    if (timedOut) {
      throw new ApiError(`接口请求超过 ${REQUEST_TIMEOUT_MS / 1000} 秒`, 408, 'CLIENT_TIMEOUT')
    }
    if (signal?.aborted) throw error
    throw new ApiError('无法连接 stockAPI，请确认服务已启动', 0, 'NETWORK_ERROR')
  } finally {
    window.clearTimeout(timer)
    signal?.removeEventListener('abort', forwardAbort)
  }
  const payload = await response.json().catch(() => null)
  if (!response.ok || !payload || payload.code !== 0) {
    throw new ApiError(
      payload?.message ?? `请求失败 (${response.status})`,
      response.status,
      payload?.error_code
    )
  }
  return payload as ApiResponse<T>
}

export function fetchDashboard(
  contract: string,
  period: string,
  signal?: AbortSignal
): Promise<ApiResponse<DashboardData>> {
  const query = new URLSearchParams({
    contract,
    period,
    bar_limit: '180',
    include_breadth: 'true'
  })
  return getJson<DashboardData>(`/api/v1/im/dashboard?${query}`, signal)
}

export function fetchInstrumentProfile(
  signal?: AbortSignal
): Promise<ApiResponse<InstrumentProfile>> {
  return getJson<InstrumentProfile>('/api/v1/im/profile', signal)
}

export async function updateInstrumentProfile(
  product: string,
  signal?: AbortSignal
): Promise<ApiResponse<InstrumentProfile>> {
  const response = await fetch(apiUrl('/api/v1/im/profile'), {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ product }),
    signal
  })
  const payload = await response.json().catch(() => null)
  if (!response.ok || !payload || payload.code !== 0) {
    throw new ApiError(
      payload?.message ?? `切换品种失败 (${response.status})`,
      response.status,
      payload?.error_code
    )
  }
  return payload as ApiResponse<InstrumentProfile>
}

export function fetchTermStructure(
  product: string,
  signal?: AbortSignal
): Promise<ApiResponse<TermStructureData>> {
  const query = new URLSearchParams({ product })
  return getJson<TermStructureData>(`/api/v1/im/term-structure?${query}`, signal)
}

export function fetchBreadth(
  universe: string,
  signal?: AbortSignal
): Promise<ApiResponse<BreadthData>> {
  const query = new URLSearchParams({ universe })
  return getJson<BreadthData>(`/api/v1/im/breadth?${query}`, signal)
}

export function fetchMemberPositions(
  contract: string,
  signal?: AbortSignal
): Promise<ApiResponse<MemberPositionsData>> {
  return getJson<MemberPositionsData>(`/api/v1/im/member-positions?contract=${encodeURIComponent(contract)}`, signal)
}

export function fetchOptionSummary(
  product: string,
  expiry: string,
  signal?: AbortSignal
): Promise<ApiResponse<OptionSummaryData>> {
  const query = new URLSearchParams({ product, expiry })
  return getJson<OptionSummaryData>(`/api/v1/im/option-summary?${query}`, signal)
}

export function fetchOptionChain(
  product: string,
  expiry: string,
  signal?: AbortSignal
): Promise<ApiResponse<OptionChainData>> {
  const query = new URLSearchParams({ product, expiry })
  return getJson<OptionChainData>(`/api/v1/im/option-chain?${query}`, signal)
}

export async function fetchChanStructure(
  contract: string,
  period: string,
  count: number,
  signal?: AbortSignal
): Promise<ChanStructureData> {
  const query = new URLSearchParams({
    code: contract,
    period,
    count: String(Math.max(60, Math.min(1000, count))),
    adjust: 'none',
    preset: 'classic'
  })
  const response = await fetch(apiUrl(`/api/chan/v2/structure?${query}`), {
    headers: { Accept: 'application/json' },
    signal
  })
  const payload = await response.json().catch(() => null)
  if (!response.ok || !payload || payload.result !== 'OK') {
    throw new ApiError(
      payload?.message ?? payload?.detail ?? `缠论计算失败 (${response.status})`,
      response.status,
      payload?.error_code
    )
  }
  return payload as ChanStructureData
}

type StreamHandlers = {
  ready: (payload: StreamReadyPayload) => void
  quotes: (payload: StreamQuotePayload) => void
  context?: (payload: StreamContextPayload) => void
  heartbeat?: () => void
}

function parseStreamBlock(block: string): { event: string; data: string; id?: string } | null {
  let event = 'message'
  let id: string | undefined
  const data: string[] = []
  for (const rawLine of block.split('\n')) {
    const line = rawLine.trimEnd()
    if (!line || line.startsWith(':')) continue
    if (line.startsWith('event:')) event = line.slice(6).trim()
    else if (line.startsWith('data:')) data.push(line.slice(5).trimStart())
    else if (line.startsWith('id:')) id = line.slice(3).trim()
  }
  return data.length ? { event, data: data.join('\n'), id } : null
}

export async function openMarketStream(
  contract: string,
  spotIndex: string,
  handlers: StreamHandlers,
  signal: AbortSignal
): Promise<void> {
  const query = new URLSearchParams({
    contracts: contract,
    spot_index: spotIndex
  })
  const response = await fetch(apiUrl(`/api/v1/im/stream?${query}`), {
    headers: { Accept: 'text/event-stream' },
    cache: 'no-store',
    signal
  })
  if (!response.ok || !response.body) {
    const payload = await response.json().catch(() => null)
    throw new ApiError(payload?.message ?? '实时通道不可用', response.status, payload?.error_code)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (!signal.aborted) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n')
    let boundary = buffer.indexOf('\n\n')
    while (boundary >= 0) {
      const block = buffer.slice(0, boundary)
      buffer = buffer.slice(boundary + 2)
      const parsed = parseStreamBlock(block)
      if (parsed) {
        try {
          const envelope = JSON.parse(parsed.data) as ApiResponse<unknown>
          if (envelope.code !== 0) throw new Error(envelope.message)
          if (parsed.event === 'ready') handlers.ready(envelope.data as StreamReadyPayload)
          if (parsed.event === 'quotes') handlers.quotes(envelope.data as StreamQuotePayload)
          if (parsed.event === 'context') handlers.context?.(envelope.data as StreamContextPayload)
          if (parsed.event === 'heartbeat') handlers.heartbeat?.()
        } catch {
          // A malformed frame must not take down the rest of the stream.
        }
      }
      boundary = buffer.indexOf('\n\n')
    }
  }
}
